import def from "csi/def";
import Action from "csi/def/Action";
import { Field } from "csi/def/Field";
import Relationship from "csi/def/Relationship";
import ViewRef from "csi/def/ViewRef";
import Widget from "csi/form/Widget";
import locale from "csi/locale";
import csi from "csi/main";
import reflection from "csi/reflection";
import { REVISION_APPROVED, REVISION_DRAFT, REVISION_PENDING } from "csi/reflection/reflectionConstants";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import { resolveNode } from "csi/service/nodeService";

// Fields
const SUBTYPE = "Subtype";

// Recipe fields
const RECIPE = "Recipe";
const RECIPE_REVISION = "RecipeRevision";

// Recipe views
const RECIPE_VIEW = "RecipeDataSheets";
const RECIPE_REVISIONS_VIEW = "FoodLabelNewFromRecipeRevisions";

// FoodBOM fields
const FOOD_BOM = "FoodBOM";
const FOOD_BOM_REVISION = "FoodBOMRevision";

// FoodBOM views
const FOOD_BOM_VIEW = "FoodBOMs";
const FOOD_BOM_REVISIONS_VIEW = "FoodLabelNewFromFoodBOMRevisions";

const NewFromBOMForm = declare(Widget, {

	bom: null,
	bomRev: null,
	bomType: null,
	bomRevType: null,
	bomView: null,
	bomRevView: null,
	bomUrl: csi.EMPTY_URL,

	_isFoodBOM: false,

	/**
	 * @override
	 * Insert Subtype, BOM, and BOMRevision fields based on BOM Type.
	 * Disable Revision field initially
	 */
	_resolveView() {
		this.insertFieldIfNotExist(reflection.ATTR_SUBTYPE, 0);
		this.defView.getField(SUBTYPE).idParameter = SUBTYPE;

		this._isFoodBOM = this.bomType === FOOD_BOM;
		this.bom = this._isFoodBOM ? FOOD_BOM : RECIPE;
		this.bomRev = this._isFoodBOM ? FOOD_BOM_REVISION : RECIPE_REVISION;
		this.bomView = this._isFoodBOM ? FOOD_BOM_VIEW : RECIPE_VIEW;
		this.bomRevView = this._isFoodBOM ? FOOD_BOM_REVISIONS_VIEW : RECIPE_REVISIONS_VIEW;
		
		const { bom, bomRev } = this;
		this.insertFieldIfNotExist(bom, 1, {
			m_typeControl: def.FIELD_FILTERING_SELECT,
			idParameter: bom,
			m_bRequired: true,
			m_onChange: "_onChangeBOM",
			m_label: locale.type(bom),
		});

		this.insertFieldIfNotExist(bomRev, 2, {
			m_typeControl: def.FIELD_FILTERING_SELECT,
			idParameter: bomRev,
			m_bRequired: true,
			m_label: locale.type(bomRev),
		});

		this.setFieldDisabled(locale.type(bomRev), true);
		this.inherited(arguments);
	},

	/**
	 * @override
	 * Add Go to Dialog to BOM and BOMRevision fields
	 */
	getFieldStore(field: Field, value: any) {
		if (field.m_id === RECIPE || field.m_id === FOOD_BOM) {
			const ctx = this.getContext();
			const foodUrl = this.defView.getSource().m_urlParent;

			// If FoodBOM, then only query for FoodBOMs with a non-dummy valued BOMLabelSubtype.
			const q = new Query().RefAttr("__Parent__", xml.OP_EQUALS, foodUrl);
			if (this._isFoodBOM) {
				q.RefAttr("BOMLabelSubtype", xml.OP_NOT_EQUALS, csi.EMPTY_URL, "Child:Subtype/Child:Settings");
			}
			const rel = lang.mixin(new Relationship(), {
				m_typeNode: this.bomType,
				m_query: q,
			});

			const store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);

			// create view for goto dialog
			store.action = lang.mixin(new Action(), {
				m_viewRef: lang.mixin(new ViewRef(), {
					m_id: this.bomView,
					m_url: foodUrl,
				}),
			});
			return store;
		} else if (field.m_id === RECIPE_REVISION || field.m_id === FOOD_BOM_REVISION) {
			const ctx = this.getContext();
			const query = new Query().RefAttr("__Parent__", xml.OP_EQUALS, this.bomUrl);
			query.Or().Attr("State", xml.OP_EQUALS, REVISION_DRAFT)
				.Attr("State", xml.OP_EQUALS, REVISION_APPROVED)
				.Attr("State", xml.OP_EQUALS, REVISION_PENDING);

			// create relationship for BOMRevision dropdown
			const rel = lang.mixin(new Relationship(), {
				m_typeNode: this.bomRevType,
				m_query: query,
			});

			const store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);

			// create view for goto dialog
			store.action = lang.mixin(new Action(), {
				m_viewRef: lang.mixin(new ViewRef(), {
					m_id: this.bomRevView,
					m_url: this.bomUrl,
				}),
				m_tooltip: locale.resource("GoToDialogPopup_Title", [locale.resource(this.bomRev)]),
			});
			return store;
		}
		return this.inherited(arguments);
	},

	/**
	 * @override
	 * BOMRevision field is made editable when BOM is set
	 */
	async _onChangeBOM(formView: any, field: any, value: string) {
		this.bomUrl = value;
		const bomRevField = locale.type(this.bomRev);
		this.setFieldDisabled(bomRevField, (value === csi.EMPTY_URL));

		const fieldBOMRevision = this.defView.getField(bomRevField);
		const store = this.getFieldStore(fieldBOMRevision, null);
		this.setFieldStore(fieldBOMRevision, store);

		let newRevisionValue = csi.EMPTY_URL;

		// get CurrentRevision to initialize BOMRevison field
		if (xml.isValidURL(this.bomUrl)) {
			const curRev = await this._getSelectedBOMRev();
			const revState = curRev.State;
			if (revState == REVISION_DRAFT || revState == REVISION_APPROVED || revState == REVISION_PENDING) {
				newRevisionValue = curRev.$URL;
			}
		}
		this.setFieldValue(fieldBOMRevision, newRevisionValue);
	},

	/**
	 * Returns CurrentRevision of selected BOM on form.
	 * @param url - selected FoodBOM/RecipeDataSheet on form
	 */
	async _getSelectedBOMRev() {
		if (!this.bomUrl) {
			return;
		}
		const bom = await resolveNode(this.bomUrl, this.cacheView);
		return await resolveNode(bom?.CurrentRevision, this.cacheView);
	},
});

export = NewFromBOMForm;